// Engagements Database
// Filter taxonomy and all engagement data

export const filterTaxonomy = {
  industries: [
    "Telecommunications",
    "Healthcare & Pharma",
    "Utilities & Energy",
    "Construction & Restoration",
    "Property Management",
    "Tax & Financial Services",
    "Home Building",
    "eCommerce",
    "Insurance",
    "Public Health",
    "Accessibility Services",
    "Consulting",
    "Education",
    "Manufacturing",
    "Real Estate",
    "Environmental Services",
    "Networking Hardware",
    "Electronics Distribution",
    "Health & Wellness",
    "Promotional Products",
    "Retail"
  ],
  capabilities: [
    "AI Strategy & Implementation",
    "Digital Transformation",
    "Salesforce Implementation",
    "Analytics & Data Platforms",
    "Process Automation",
    "Program Management",
    "Agile Transformation",
    "Change Management",
    "Training & Enablement",
    "Solution Ownership",
    "Business Analysis",
    "Process Improvement",
    "Sales Enablement"
  ],
  technologies: [
    "Python",
    "SQL",
    "MySQL",
    "Linux",
    "n8n",
    "Make",
    "Voice AI / Retell",
    "RAG / Vector DBs",
    "MCP",
    "LLMs",
    "Salesforce",
    "Pardot",
    "Azure DevOps",
    "Airtable",
    "HousecallPro",
    "Adobe Experience Platform",
    "Datorama",
    "Jira",
    "FIDO2",
    "Prisma Access",
    "GlobalProtect",
    "ZoomInfo",
    "ERP Integrations",
    "nopCommerce",
    "Solr",
    "SSIS",
    "SQL Server",
    "Git/Bitbucket",
    "ASPdotNetStorefront",
    "Nextopia",
    "WordPress",
    "Kentico",
    "Vue.js",
    "VTiger CRM",
    "Google Maps API",
    "Sharpspring",
    "Docker",
    "Tailwind CSS",
    "Selenium",
    "Jenkins",
    "ElasticSearch",
    "Shopify",
    "Quasar",
    "Heroku"
  ]
};

export const engagements = [
  {
    id: 1,
    title: "Voice AI Recruiting System",
    client: "National Telecommunications Dealer",
    duration: "Mar 2025 – Present",
    role: "AI Strategy & Implementation Consultant",
    industry: "Telecommunications",
    capabilities: ["AI Strategy & Implementation", "Process Automation"],
    technologies: ["Python", "n8n", "Voice AI / Retell", "LLMs", "RAG / Vector DBs", "MCP", "Airtable"],
    engagementType: "Implementation",
    keyMetric: "700+ daily interactions",
    context: "National telecommunications dealer with 100+ field sales representatives needed to scale candidate recruiting and screening without adding headcount. Manual phone screening was creating bottlenecks and inconsistent candidate experiences.",
    approach: "Designed and built end-to-end AI recruiting system from technology assessment through production deployment. Architected voice AI pipeline integrating conversational AI, workflow automation, CRM, database, and calendar scheduling. Implemented post-call analysis to extract qualification data and sentiment. Optimized costs through prompt engineering and model selection.",
    architecture: "Retell AI for voice conversations, n8n for workflow orchestration, Zoho Recruit for CRM, PostgreSQL for data storage, Google Calendar API for scheduling, LLM-based post-call analysis extracting 22 data points per conversation, Custom MCP servers for natural language data queries.",
    outcomes: [
      "700+ daily candidate interactions processed automatically",
      "Cost per interaction reduced from $4 to under $2 (50%+ reduction)",
      "22 qualification and sentiment data points extracted per conversation",
      "System running in production with ongoing optimization"
    ]
  },
  {
    id: 2,
    title: "Property Management Scheduling Automation",
    client: "Vacation Rental Services Company",
    duration: "Apr 2025 – Jun 2025",
    role: "AI Strategy & Implementation Consultant",
    industry: "Property Management",
    capabilities: ["Process Automation", "AI Strategy & Implementation"],
    technologies: ["Python", "MCP", "Airtable", "HousecallPro"],
    engagementType: "Implementation",
    keyMetric: "100+ properties automated",
    context: "Cleaning services company managing 100+ vacation rental properties was manually creating cleaning jobs from calendar bookings across Airbnb, VRBO, Booking.com, HostTools, and Lodgify. Process was error-prone and time-consuming.",
    approach: "Built scheduling automation platform that ingests ICS calendar files from multiple sources, detects reservations vs. owner blocks, applies business logic for same-day turnovers and late checkouts, and automatically creates/updates jobs in HousecallPro.",
    architecture: "Custom MCP servers for Airtable and HousecallPro enabling natural language CRUD operations, Python automation scripts for calendar ingestion and processing, Selenium for web scraping property management platforms, Webhook integrations for real-time sync, Admin interface in Airtable for non-technical staff.",
    outcomes: [
      "100+ properties automated",
      "Manual job creation eliminated",
      "Real-time sync between reservation systems and job scheduling",
      "Documentation and video training delivered for ongoing operations"
    ]
  },
  {
    id: 3,
    title: "Enterprise Authentication Modernization",
    client: "Major Regional Utility",
    duration: "2024 – Present",
    role: "Project Manager, Scrum Master",
    industry: "Utilities & Energy",
    capabilities: ["Program Management", "Agile Transformation"],
    technologies: ["Azure DevOps", "FIDO2"],
    engagementType: "Implementation",
    keyMetric: "$500K → $1.3M expansion",
    context: "Major utility company needed to modernize multi-factor authentication for 13,000+ users while improving security posture and user experience.",
    approach: "Led cross-functional engineering and security teams through Azure DevOps. Gathered business requirements, refined technical specifications, and aligned authentication strategies with enterprise objectives. Developed strategic roadmap that identified expansion opportunities.",
    architecture: "FIDO2-based passwordless authentication, Azure DevOps for project management and delivery, Integration with existing identity infrastructure.",
    outcomes: [
      "Program expanded from $500K to $1.3M through strategic roadmap development",
      "13,000+ users impacted",
      "FIDO2 passwordless authentication successfully implemented",
      "Cross-functional delivery across engineering and security teams"
    ]
  },
  {
    id: 4,
    title: "Prisma Access VPN / ZTNA Migration",
    client: "Major Regional Utility",
    duration: "2024 – Present",
    role: "Business Analyst, UAT Coordinator",
    industry: "Utilities & Energy",
    capabilities: ["Program Management", "Business Analysis"],
    technologies: ["Azure DevOps", "Prisma Access", "GlobalProtect"],
    engagementType: "Implementation",
    keyMetric: "9,800 users migrated",
    context: "Major utility needed to migrate 9,800 users from legacy Ivanti VPN (end-of-life) to a modern Zero Trust Network Access (ZTNA) architecture.",
    approach: "Supporting complex infrastructure migration involving multiple technical challenges: BGP/OSPF routing integration, Equinix private edge connectivity, dual management platform coordination, and IP subnet architecture for 9,800 users across 10+ gateways.",
    architecture: "Prisma Access for cloud-delivered security, GlobalProtect client deployment via SCCM, Service connections with BGP peering to edge routers, SAML authentication via Entra ID, SailPoint/IAM integration for role-based access.",
    outcomes: [
      "9,800 users impacted",
      "Migration from legacy VPN to modern ZTNA architecture",
      "UAT phase coordinated across Solution Leads, Architects, and Engineering",
      "Test cases documented for 30+ security and functional requirements"
    ]
  },
  {
    id: 5,
    title: "Digital Transformation & Salesforce CRM",
    client: "National Restoration Services Firm",
    duration: "14 months (3 engagements)",
    role: "Solution Owner, Business Analyst",
    industry: "Construction & Restoration",
    capabilities: ["Digital Transformation", "Salesforce Implementation", "Change Management", "Solution Ownership"],
    technologies: ["Salesforce", "Pardot", "ZoomInfo", "ERP Integrations"],
    engagementType: "Strategy & Roadmap, Implementation",
    keyMetric: "300+ users across 19 branches",
    context: "National emergency restoration services firm needed comprehensive digital transformation across sales, marketing, and operations. Multiple systems were disconnected and processes were inconsistent across 19 branches.",
    approach: "Led strategic assessment through 23+ hours of workshops. Delivered current state assessment, gap analysis, and strategic roadmap. Implemented Salesforce CRM with ERP integrations. Deployed Pardot for marketing automation with ZoomInfo integration.",
    architecture: "Salesforce CRM with Sage 100 ERP integration, Pardot marketing automation, ZoomInfo lead enrichment.",
    outcomes: [
      "23+ hours of stakeholder workshops facilitated",
      "100+ documents analyzed for current state assessment",
      "Salesforce CRM successfully implemented across 19 branches",
      "300+ users adopted the platform",
      "Marketing automation achieved through Pardot and ZoomInfo"
    ]
  },
  {
    id: 6,
    title: "CDP Strategy & Planning",
    client: "Fortune 500 Pharmaceutical Company",
    duration: "5 months",
    role: "Solution Owner",
    industry: "Healthcare & Pharma",
    capabilities: ["Analytics & Data Platforms", "AI Strategy & Implementation", "Solution Ownership"],
    technologies: ["Adobe Experience Platform", "Datorama"],
    engagementType: "Strategy & Roadmap",
    keyMetric: "35+ hours stakeholder interviews",
    context: "Fortune 500 pharmaceutical company needed customer data platform strategy to unify customer data across multiple business units and enable AI-driven use cases.",
    approach: "Conducted 35+ hours of stakeholder interviews across 5-8 business units. Mapped current and future states, identified gaps, and created use case roadmaps. Defined technical requirements and platform capabilities.",
    architecture: null,
    outcomes: [
      "35+ hours of executive stakeholder interviews conducted",
      "AI-driven use case identification across business units",
      "Strategic influence on enterprise platform capabilities and roadmap",
      "MVP prioritization completed to accelerate decision-making"
    ]
  },
  {
    id: 7,
    title: "Datorama Analytics Optimization",
    client: "Fortune 500 Pharmaceutical Company",
    duration: "2 months",
    role: "Delivery Leader",
    industry: "Healthcare & Pharma",
    capabilities: ["Analytics & Data Platforms", "Business Analysis"],
    technologies: ["Datorama"],
    engagementType: "Assessment, Strategy & Roadmap",
    keyMetric: "20+ stakeholder interviews",
    context: "Global life sciences organization needed to optimize their Datorama analytics platform to improve reporting capabilities and user adoption.",
    approach: "Led all activities as Delivery Leader. Conducted 20+ stakeholder interviews across departments. Analyzed user personas to define needs and gaps. Identified themes around data optimization and user empowerment.",
    architecture: null,
    outcomes: [
      "20+ stakeholder interviews conducted",
      "Multi-phase roadmap developed integrating user feedback",
      "Enhanced reporting capabilities identified",
      "Educational materials and best practices created"
    ]
  },
  {
    id: 8,
    title: "Salesforce CRM Implementation",
    client: "National Tax Services Firm",
    duration: "6 months",
    role: "Solution Owner",
    industry: "Tax & Financial Services",
    capabilities: ["Salesforce Implementation", "Change Management", "Agile Transformation"],
    technologies: ["Salesforce"],
    engagementType: "Implementation",
    keyMetric: "Sales conversion doubled",
    context: "Tax services firm had experienced three failed vendor attempts at Salesforce implementation. Sales leadership urgently needed a platform that would drive adoption and improve conversion rates.",
    approach: "Led discovery workshops to understand why previous attempts failed. Developed strategic roadmap focused on user adoption and business outcomes. Coached CEO and Product Owner on Agile practices. Delivered implementation with change management focus.",
    architecture: null,
    outcomes: [
      "Sales conversion rates doubled",
      "$750K in change orders secured through roadmap expansion",
      "Successful delivery after three failed vendor attempts",
      "C-level coaching on Agile practices delivered"
    ]
  },
  {
    id: 9,
    title: "Digital Transformation Assessment",
    client: "National Homebuilder",
    duration: "5 weeks",
    role: "Solution Owner",
    industry: "Home Building",
    capabilities: ["Digital Transformation", "Business Analysis"],
    technologies: [],
    engagementType: "Assessment, Strategy & Roadmap",
    keyMetric: "35+ stakeholder interviews",
    context: "National homebuilder needed comprehensive assessment of technology landscape and roadmap for digital transformation focused on customer journey.",
    approach: "Conducted interviews with 35+ stakeholders across functions. Reviewed and analyzed 100+ documents. Mapped epics, features, and user stories. Produced strategic technology roadmap integrating data-driven initiatives with business objectives.",
    architecture: null,
    outcomes: [
      "35+ stakeholder interviews conducted",
      "100+ documents analyzed",
      "Comprehensive technology recommendations delivered",
      "Future state architecture defined"
    ]
  },
  {
    id: 10,
    title: "AI Navigator Program",
    client: "Slalom (Internal)",
    duration: "2024 – Present",
    role: "AI Navigator",
    industry: "Consulting",
    capabilities: ["Training & Enablement", "AI Strategy & Implementation"],
    technologies: ["LLMs", "Azure", "Microsoft Copilot"],
    engagementType: "Training",
    keyMetric: "Firm-wide curriculum adoption",
    context: "Slalom consultants needed practical training on emerging AI tools and patterns to better serve clients exploring AI adoption.",
    approach: "Selected as AI Navigator to train Business Advisory Services consultants. Developed comprehensive training curriculum and assessment frameworks. Delivered hands-on workshops on practical AI applications.",
    architecture: null,
    outcomes: [
      "Training curriculum adopted across the firm",
      "AI readiness framework used in client discovery engagements",
      "Ongoing enablement for consultants on emerging AI capabilities"
    ]
  },
  {
    id: 11,
    title: "Street Medicine Platform RFP",
    client: "Public Health Organization",
    duration: "2024",
    role: "RFP Lead",
    industry: "Public Health",
    capabilities: ["Program Management", "Solution Ownership"],
    technologies: [],
    engagementType: "RFP Response",
    keyMetric: "AI-enabled solution design",
    context: "Public health organization needed comprehensive technical solution design for street medicine platform incorporating innovative AI-enabled capabilities.",
    approach: "Led RFP response process. Coordinated cross-functional teams to develop technical solution design. Incorporated AI-enabled capabilities into proposal.",
    architecture: null,
    outcomes: [
      "Cross-functional RFP response delivered",
      "Technical solution design incorporating AI capabilities",
      "Proposal development and submission completed"
    ]
  },
  {
    id: 12,
    title: "Agile Estimation Workshop",
    client: "National Insurance Company",
    duration: "2023",
    role: "Agile Coach",
    industry: "Insurance",
    capabilities: ["Agile Transformation", "Training & Enablement"],
    technologies: ["Jira"],
    engagementType: "Training",
    keyMetric: "Team estimation practices improved",
    context: "Insurance company teams needed structured approach to Agile estimation practices.",
    approach: "Developed and led Agile estimation workshop. Coached teams on story writing, sprint planning, and backlog refinement.",
    architecture: null,
    outcomes: [
      "Agile estimation workshop delivered",
      "Teams trained on estimation best practices"
    ]
  },
  {
    id: 13,
    title: "Agile Transformation",
    client: "Accessibility Services Provider",
    duration: "2022-2023",
    role: "Scrum Master, Agile Coach",
    industry: "Accessibility Services",
    capabilities: ["Agile Transformation"],
    technologies: ["Jira"],
    engagementType: "Implementation",
    keyMetric: "Agile practices implemented",
    context: "Telecommunications/accessibility services company needed Agile transformation support.",
    approach: "Transitioned development teams to Agile methodologies. Led sprint planning, retrospectives, and backlog refinement.",
    architecture: null,
    outcomes: [
      "Agile practices implemented",
      "Team coaching delivered"
    ]
  },
  {
    id: 14,
    title: "HR Generative AI Use Cases",
    client: "Slalom (Internal/Multiple)",
    duration: "2023-2024",
    role: "AI Strategy Consultant",
    industry: "Consulting",
    capabilities: ["AI Strategy & Implementation"],
    technologies: ["LLMs"],
    engagementType: "Strategy & Roadmap",
    keyMetric: "20+ use cases defined",
    context: "Developed AI use cases for HR functions across multiple industries.",
    approach: "Authored comprehensive document on AI-driven HR use cases. Created AI readiness assessment frameworks.",
    architecture: null,
    outcomes: [
      "Use cases adopted for client engagements",
      "Framework for HR AI assessment created",
      "20+ HR AI-driven use cases defined"
    ]
  },
  {
    id: 15,
    title: "eCommerce Platform Management",
    client: "Digital Agency",
    duration: "Mar 2019 – Jul 2021",
    role: "Digital Project Manager",
    industry: "eCommerce",
    capabilities: ["Program Management", "Solution Ownership"],
    technologies: ["Jira"],
    engagementType: "Implementation",
    keyMetric: "$500K annual portfolio",
    context: "Digital agency managing $500K annually in custom development and design projects across multiple eCommerce platforms.",
    approach: "Product owner of baseline sites featuring continuous deployment, automation testing, and reusable feature libraries. Coordinated offshore and in-house teams.",
    architecture: null,
    outcomes: [
      "20+ client projects delivered using reusable feature libraries",
      "$500K annual project portfolio managed",
      "Custom analytics tracking implemented"
    ]
  },
  {
    id: 16,
    title: "SaaS eCommerce Implementations",
    client: "SaaS eCommerce Platform",
    duration: "May 2016 – Mar 2019",
    role: "SaaS eCommerce Project Manager",
    industry: "eCommerce",
    capabilities: ["Program Management", "Agile Transformation"],
    technologies: ["MySQL", "SQL", "Linux", "Jira"],
    engagementType: "Implementation",
    keyMetric: "80% time reduction",
    context: "SaaS eCommerce platform implementations ranging from $10K to $300K.",
    approach: "Led website launches while balancing stakeholder expectations, budget, and timeline. Coached teams on Agile methodologies. Self-taught MySQL and Linux server administration to create automated processes.",
    architecture: null,
    outcomes: [
      "Successful eCommerce launches delivered ($10K-$300K range)",
      "Site creation time reduced by 80% through automated database cloning",
      "Agile practices implemented across teams"
    ]
  },
  // === SLALOM INTERNAL CONTRIBUTIONS ===
  {
    id: 17,
    title: "Delivery Excellence Templates",
    client: "Slalom (Internal)",
    duration: "2022-2024",
    role: "BAS Delivery Excellence Representative",
    industry: "Consulting",
    capabilities: ["Process Improvement", "Training & Enablement"],
    technologies: [],
    engagementType: "Internal Contribution",
    keyMetric: "1,200+ views firm-wide",
    context: "Slalom needed standardized templates for consistent project delivery across the firm.",
    approach: "Created comprehensive project kickoff and closeout templates that established delivery standards and best practices.",
    architecture: null,
    outcomes: [
      "Award-winning templates adopted globally",
      "1,200+ views across the firm",
      "Recognized as delivery excellence standard",
      "Served as BAS Delivery Excellence Representative"
    ]
  },
  {
    id: 18,
    title: "Agile Framework Standardization",
    client: "Slalom (Internal)",
    duration: "2022-2024",
    role: "Agile Coach",
    industry: "Consulting",
    capabilities: ["Agile Transformation", "Process Improvement"],
    technologies: ["Jira", "Git/Bitbucket"],
    engagementType: "Internal Contribution",
    keyMetric: "Enterprise blueprint adopted",
    context: "Enterprise teams needed consistent Agile delivery frameworks across implementations.",
    approach: "Created standardized Agile delivery frameworks and Kanban implementations. Participated in internal Agile Coaching Cohorts.",
    architecture: null,
    outcomes: [
      "Standardized frameworks using JIRA and Bitbucket became blueprint for multiple enterprise implementations",
      "Kanban implementation recognized as 'excellent example' during enterprise Agile Coaching Cohort",
      "Mentored colleagues on Agile methodologies"
    ]
  },
  {
    id: 19,
    title: "Salesforce Data Cloud Enablement",
    client: "Slalom (Internal)",
    duration: "2023-2024",
    role: "Sales Enablement Contributor",
    industry: "Consulting",
    capabilities: ["Sales Enablement", "Solution Ownership"],
    technologies: ["Salesforce"],
    engagementType: "Internal Contribution",
    keyMetric: "Customer stories developed",
    context: "Salesforce Data Cloud team needed compelling customer stories for client pitches.",
    approach: "Supported team in humanizing customer stories and developing sales materials.",
    architecture: null,
    outcomes: [
      "Customer stories developed for client pitches",
      "Sales enablement materials created"
    ]
  },
  // === DIGITAL AGENCY ERA (2019-2021) ===
  {
    id: 20,
    title: "Multi-Brand eCommerce Platform",
    client: "Networking Hardware Distributor",
    duration: "2019-2021",
    role: "Technical Project Manager",
    industry: "Networking Hardware",
    capabilities: ["Solution Ownership", "Program Management"],
    technologies: ["nopCommerce", "Solr", "SSIS", "SQL Server", "Git/Bitbucket", "Pardot"],
    engagementType: "Implementation",
    keyMetric: "Multi-site platform managed",
    context: "Multi-site eCommerce platform requiring cloning, enhancement, and ongoing maintenance across multiple networking hardware brands.",
    approach: "Managed cloning of nopCommerce sites across brands. Developed and maintained SSIS packages for product data import, URL records, and distributor feeds. Configured and optimized Solr search integration. Managed Git workflow and multi-environment deployments.",
    architecture: "nopCommerce platform with Solr search, SSIS data integration packages, multi-environment deployment pipeline.",
    outcomes: [
      "Multi-site eCommerce platform successfully maintained",
      "Product data integrations automated via SSIS",
      "Search optimization and performance improvements delivered",
      "Server migration and environment management completed"
    ]
  },
  {
    id: 21,
    title: "Electronics Distributor UI/UX Redesign",
    client: "RF & Microwave Components Distributor",
    duration: "2020-2021",
    role: "Project Manager",
    industry: "Electronics Distribution",
    capabilities: ["Program Management", "Solution Ownership"],
    technologies: ["ASPdotNetStorefront", "Nextopia", "Git/Bitbucket"],
    engagementType: "Implementation",
    keyMetric: "Major UI/UX redesign launched",
    context: "Large-scale eCommerce website UI/UX redesign and redevelopment for RF & microwave components distributor.",
    approach: "Managed entire project lifecycle from kickoff to post-launch support. Translated complex UI/UX designs into actionable development tasks. Coordinated multi-server deployments with load balancing. Implemented gradual rollout functionality.",
    architecture: "ASPdotNetStorefront platform, Nextopia predictive search, multi-server load-balanced deployment.",
    outcomes: [
      "Major UI/UX redesign successfully launched",
      "Responsive global theme and redesigned homepage delivered",
      "Complex mega menu with alphabetical sorting implemented",
      "Advanced PLP filters/facets and multi-step checkout deployed",
      "Comprehensive launch plan executed across multiple servers"
    ]
  },
  {
    id: 22,
    title: "Digital Admissions Viewbook",
    client: "Trade School Education Provider",
    duration: "Jul 2020 – Sep 2020",
    role: "Project Manager",
    industry: "Education",
    capabilities: ["Solution Ownership", "Program Management"],
    technologies: ["WordPress", "Google Maps API"],
    engagementType: "Implementation",
    keyMetric: "Follow-on contract secured",
    context: "Trade school needed dynamic digital admissions tool for advisors to help convince prospective students to enroll.",
    approach: "Led discovery sessions to define goals and functionality. Managed WordPress-based application with custom Gutenberg blocks. Implemented dynamic content display based on campus/program selections. Coordinated content gathering and UAT.",
    architecture: "WordPress with custom Gutenberg blocks, Vimeo video integration, Google Maps campus locator.",
    outcomes: [
      "Source code and database delivered on time",
      "Secured follow-on contract for additional school",
      "Dynamic content filtering by campus and program implemented"
    ]
  },
  {
    id: 23,
    title: "K-12 School District CMS Upgrade",
    client: "K-12 School District",
    duration: "2020",
    role: "Project Manager",
    industry: "Education",
    capabilities: ["Program Management"],
    technologies: ["Kentico"],
    engagementType: "Implementation",
    keyMetric: "Zero-downtime migration",
    context: "School district needed CMS version upgrade from Kentico V10 to V12.",
    approach: "Planned multi-step upgrade process (V10→V11→V12). Coordinated development environment setup and testing. Managed production upgrade with minimal downtime.",
    architecture: null,
    outcomes: [
      "Successful upgrade to Kentico V12",
      "Minimal downtime during production migration",
      "Post-launch optimization recommendations provided"
    ]
  },
  {
    id: 24,
    title: "Medicare Advantage Document Manager",
    client: "Medicare Advantage Health Plan",
    duration: "2020",
    role: "Project Manager",
    industry: "Healthcare & Pharma",
    capabilities: ["Solution Ownership", "Program Management"],
    technologies: ["Kentico", "Vue.js"],
    engagementType: "Implementation",
    keyMetric: "Custom document system deployed",
    context: "Healthcare organization needed custom document management system with complex filtering for Medicare Advantage plan documents.",
    approach: "Designed solution for bulk uploading and tagging documents (Year, County, Plan, Type, Language). Made key decision to use Vue.js for front-end filtering (vs. Kentico postbacks). Coordinated development of custom Kentico backend with API endpoints.",
    architecture: "Kentico CMS backend with custom API endpoints, Vue.js front-end for dynamic filtering, bulk PDF upload with drag & drop.",
    outcomes: [
      "Custom document management system deployed",
      "Vue.js filtering improved user experience",
      "Complex business logic implemented for plan/county/year filtering",
      "Bulk PDF upload with drag & drop delivered"
    ]
  },
  {
    id: 25,
    title: "Real Estate Developer Website",
    client: "Master-Planned Community Developer",
    duration: "Nov 2019 – Jul 2020",
    role: "Project Manager",
    industry: "Real Estate",
    capabilities: ["Program Management", "Solution Ownership"],
    technologies: ["WordPress"],
    engagementType: "Implementation",
    keyMetric: "Sustaining contract secured",
    context: "Real estate developer needed reimagined lifestyle brand website.",
    approach: "Managed development lifecycle from requirements to launch. Coordinated with design agency and internal teams. Implemented content sorting logic and dynamic sections. Managed SEO requirements and 301 redirects.",
    architecture: null,
    outcomes: [
      "Website successfully launched on time",
      "Sustaining contract secured for ongoing development",
      "Phase 2 enhancements scoped and delivered"
    ]
  },
  {
    id: 26,
    title: "Health Insurance Lead Generation Platform",
    client: "Health Insurance Brokerage",
    duration: "2020",
    role: "Project Manager",
    industry: "Insurance",
    capabilities: ["Solution Ownership", "Program Management"],
    technologies: ["Vue.js", "Quasar", "VTiger CRM"],
    engagementType: "Implementation",
    keyMetric: "Application stabilized",
    context: "Health insurance quoting and lead generation platform requiring rescue, refactoring, and enhancement.",
    approach: "Took ownership of unstable application and managed stabilization. Led redesign of user journey for quote request and plan selection. Integrated lead capture with VTiger CRM. Coordinated digital marketing strategies (PPC, SEM, landing pages).",
    architecture: "Vue.js with Quasar framework, VTiger CRM integration, PPC/SEM landing pages.",
    outcomes: [
      "Application stabilized and enhanced",
      "User journey simplified for better conversion",
      "CRM integration completed",
      "Marketing campaigns launched"
    ]
  },
  {
    id: 27,
    title: "Health & Wellness eCommerce Enhancements",
    client: "CBD Products eCommerce Company",
    duration: "2020",
    role: "Project Manager",
    industry: "Health & Wellness",
    capabilities: ["Solution Ownership"],
    technologies: ["nopCommerce"],
    engagementType: "Implementation",
    keyMetric: "Checkout experience improved",
    context: "CBD products eCommerce site needed checkout optimization and custom features.",
    approach: "Managed checkout flow redesign including minicart flyout panel. Coordinated payment gateway implementations. Oversaw subscription and wholesale pricing logic.",
    architecture: null,
    outcomes: [
      "Checkout experience improved",
      "Payment gateways (Google Pay, VisaCheckout) implemented",
      "Minicart redesigned with flyout functionality"
    ]
  },
  {
    id: 28,
    title: "Environmental Consulting Firm Website",
    client: "Environmental Consulting & Engineering Firm",
    duration: "2019-2020",
    role: "Project Manager",
    industry: "Environmental Services",
    capabilities: ["Program Management"],
    technologies: ["Kentico", "Google Maps API", "Sharpspring"],
    engagementType: "Implementation",
    keyMetric: "Kentico site launched",
    context: "Environmental consulting firm needed website build/migration on Kentico platform.",
    approach: "Defined information architecture and site structure. Coordinated development of staff, events, projects, and location pages. Managed Google Maps integration and responsive design optimization.",
    architecture: null,
    outcomes: [
      "Kentico website successfully launched",
      "iPad optimization issues resolved",
      "Sharpspring integration troubleshot"
    ]
  },
  {
    id: 29,
    title: "Electronics Distributor Sustaining Engineering",
    client: "Electronics Distribution Company (7+ Brands)",
    duration: "2019-2021",
    role: "Account/Project Manager",
    industry: "Electronics Distribution",
    capabilities: ["Program Management", "Solution Ownership"],
    technologies: ["ASPdotNetStorefront", "nopCommerce", "Jira", "Git/Bitbucket", "SSIS"],
    engagementType: "Implementation",
    keyMetric: "7+ brands maintained",
    context: "Ongoing maintenance, support, and enhancements across multiple electronics eCommerce brands.",
    approach: "Managed sustaining engineering retainer across 7+ brands. Coordinated sprint-based development and monthly releases. Developed code coexistence strategy for concurrent development. Troubleshot critical production issues.",
    architecture: null,
    outcomes: [
      "Multi-brand eCommerce portfolio maintained",
      "Monthly release process managed",
      "Performance issues resolved (CPU optimization, database tuning)",
      "Server maintenance and upgrades coordinated"
    ]
  },
  {
    id: 30,
    title: "HVAC Product Configurator",
    client: "HVAC Equipment Manufacturer",
    duration: "2020",
    role: "Project Manager",
    industry: "Manufacturing",
    capabilities: ["Solution Ownership"],
    technologies: ["Heroku"],
    engagementType: "Implementation",
    keyMetric: "Configurator deployed",
    context: "HVAC manufacturer needed web-based product configurator for complex air curtain products.",
    approach: "Led discovery and technical specification development. Managed integration with client-provided API. Coordinated UI/UX design for configurator workflow.",
    architecture: "Custom API hosted on Heroku, JavaScript front-end configurator.",
    outcomes: [
      "Product configurator deployed",
      "API integration completed",
      "Configurator integrated with main website PDPs"
    ]
  },
  {
    id: 31,
    title: "WordPress Baseline & Toolkit",
    client: "Digital Agency (Internal)",
    duration: "2019-2021",
    role: "Product Owner",
    industry: "Consulting",
    capabilities: ["Solution Ownership", "Process Improvement"],
    technologies: ["WordPress", "Docker", "Tailwind CSS"],
    engagementType: "Internal Contribution",
    keyMetric: "Reusable toolkit adopted",
    context: "Agency needed standardized WordPress starter theme and plugin toolkit.",
    approach: "Led development of internal WordPress starter theme (GC-Theme). Coordinated plugin development (Announcements, Hyperlocal, CF7 Extras, Schema). Managed migration from Foundation to Tailwind CSS. Created training materials and documentation.",
    architecture: "WordPress starter theme, Docker-based development environment, custom plugin library.",
    outcomes: [
      "Reusable starter theme adopted across projects",
      "Plugin toolkit maintained and enhanced",
      "Docker-based development environment established",
      "Developer training materials created"
    ]
  },
  {
    id: 32,
    title: "Automation Testing Framework",
    client: "Digital Agency (Internal)",
    duration: "2020-2021",
    role: "Initiative Lead",
    industry: "Consulting",
    capabilities: ["Process Improvement"],
    technologies: ["Selenium", "Jenkins"],
    engagementType: "Internal Contribution",
    keyMetric: "Testing framework implemented",
    context: "Agency needed automated testing solution for eCommerce projects.",
    approach: "Evaluated New Relic Synthetics, selected Selenium C# with Jenkins. Coordinated development of testing framework and helper functions. Established processes for multi-environment testing.",
    architecture: "Selenium WebDriver (C#), Jenkins CI/CD, Discord notifications.",
    outcomes: [
      "Selenium C#/Jenkins framework implemented",
      "Test suites created for NopCommerce projects",
      "Automated reporting to Discord established"
    ]
  },
  {
    id: 33,
    title: "NopCommerce Baseline & Toolkit",
    client: "Digital Agency (Internal)",
    duration: "2019-2021",
    role: "Product Owner",
    industry: "Consulting",
    capabilities: ["Solution Ownership", "Process Improvement"],
    technologies: ["nopCommerce", "Solr", "ElasticSearch"],
    engagementType: "Internal Contribution",
    keyMetric: "Plugin library maintained",
    context: "Agency needed standardized NopCommerce baseline theme and plugin toolkit.",
    approach: "Led development of GCStarter theme and toolkit plugins. Coordinated Solr v2.0 implementation for improved URL structure. Managed Form Builder, Promotions, Content Pages, SEO plugins.",
    architecture: "NopCommerce baseline theme, Solr/ElasticSearch integration, custom plugin ecosystem.",
    outcomes: [
      "Reusable NopCommerce toolkit adopted",
      "4.3 upgrade path assessed and planned",
      "Plugin library maintained across client projects",
      "Form Builder, Promotions, SEO plugins developed"
    ]
  },
  {
    id: 34,
    title: "Internal Onboarding Process Improvement",
    client: "Digital Agency (Internal)",
    duration: "2020",
    role: "Process Improvement Lead",
    industry: "Consulting",
    capabilities: ["Process Improvement", "Training & Enablement"],
    technologies: ["Jira"],
    engagementType: "Internal Contribution",
    keyMetric: "Onboarding improvements proposed",
    context: "Agency needed improved employee onboarding procedures.",
    approach: "Reviewed existing checklists and training materials. Identified outdated content and redundant tasks. Proposed department-specific onboarding paths. Researched LMS solutions.",
    architecture: null,
    outcomes: [
      "Onboarding process improvements proposed",
      "Department-specific scavenger hunts designed",
      "Training materials identified for update"
    ]
  },
  // === SAAS & ECOMMERCE ERA (2016-2019) ===
  {
    id: 35,
    title: "Promotional Products DIY Platform",
    client: "Promotional Products Startup",
    duration: "Mar 2019 – Aug 2019",
    role: "Secondary Project Manager",
    industry: "Promotional Products",
    capabilities: ["Program Management", "Solution Ownership"],
    technologies: ["nopCommerce", "Jira"],
    engagementType: "Implementation",
    keyMetric: "MVP partially completed",
    context: "Startup needed DIY promotional product generation tool for B2B and B2C customers.",
    approach: "Worked alongside lead PM in discovery and business case creation. Managed 5+ front-end and back-end developers in Jira. Phased MVP approach for budget management.",
    architecture: null,
    outcomes: [
      "Project closed due to client funding issues",
      "MVP partially completed before closure",
      "Technology stack and architecture documented"
    ]
  },
  {
    id: 36,
    title: "Floral Services Website Rebuild",
    client: "Floral Services Company",
    duration: "Sep 2019 – Nov 2019",
    role: "Project Manager",
    industry: "Retail",
    capabilities: ["Program Management"],
    technologies: ["WordPress"],
    engagementType: "Implementation",
    keyMetric: "Website launched successfully",
    context: "Business needed website rebuild on maintainable framework with SEO/SEM best practices.",
    approach: "Led discovery, production planning, and timeline creation. Managed front-end developer tasks. Created launch plan and executed successfully.",
    architecture: null,
    outcomes: [
      "Website successfully launched",
      "Transferred to Online Marketing Group for ongoing management",
      "AdWord campaigns established"
    ]
  },
  {
    id: 37,
    title: "Educational Resources eCommerce Platform",
    client: "Educational Resources Retailer",
    duration: "Jun 2017 – Mar 2019",
    role: "Scrum Master & Project Manager",
    industry: "Education",
    capabilities: ["Program Management", "Agile Transformation"],
    technologies: [],
    engagementType: "Implementation",
    keyMetric: "65% completion at transition",
    context: "B2C educational resources company needed infrastructure rebuild on eCommerce platform.",
    approach: "Led Scrum team with product backlog, sprint planning, and 1-week sprints. Managed requirements collection and WBS creation. Conducted daily standups, sprint reviews, and retrospectives.",
    architecture: null,
    outcomes: [
      "Project at 65% completion at departure",
      "Successfully launched on schedule after transition",
      "Agile practices implemented"
    ]
  },
  {
    id: 38,
    title: "Promotional Products B2B Portal",
    client: "Promotional Products Company",
    duration: "Jan 2017 – Jun 2017",
    role: "Project Manager",
    industry: "Promotional Products",
    capabilities: ["Program Management"],
    technologies: [],
    engagementType: "Implementation",
    keyMetric: "Launched on time & under budget",
    context: "Company needed revenue-generating promotional product web portal with supplier integrations and price level controls.",
    approach: "Developed project management plan and WBS. Managed design and development team. Controlled scope, schedule, and costs.",
    architecture: null,
    outcomes: [
      "B2B eCommerce site launched on time and under budget",
      "Supplier integrations completed",
      "Price level controls implemented"
    ]
  },
  {
    id: 39,
    title: "Industrial Supplies eCommerce Migration",
    client: "Industrial Supplies Distributor",
    duration: "May 2016 – Jan 2017",
    role: "Project Manager",
    industry: "eCommerce",
    capabilities: ["Program Management"],
    technologies: [],
    engagementType: "Implementation",
    keyMetric: "17% revenue increase",
    context: "Company needed to migrate online catalog to eCommerce portal for autonomous customer purchasing.",
    approach: "Collected requirements and created WBS. Managed cross-functional team of 4 resources. Used predictive project plan lifecycle.",
    architecture: null,
    outcomes: [
      "Portal delivered within budget, time, and scope",
      "17% revenue increase within 4 months of launch",
      "Successful migration from catalog to eCommerce"
    ]
  }
];
